/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's copyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import React, { forwardRef } from 'react';
import { cn } from "@/shared/ui/utils/tailwind";

type FlexProps = {
  className?: string;
  style?: React.CSSProperties;
  children?: React.ReactNode;
  [key: string]: any;
};

const NON_DOM_PROPS = new Set(['component', 'sx', 'buttonVariant', 'buttonColor', 'disableSize', 'leftBorder', 'rightBorder']);

/**
 * Strips non-DOM props before passing to native HTML elements.
 */
function filterDomProps(props: Record<string, any>): Record<string, any> {
  const result: Record<string, any> = {};
  for (const key of Object.keys(props)) {
    if (!NON_DOM_PROPS.has(key)) result[key] = props[key];
  }
  return result;
}

/**
 * Flex container.
 *
 * @param {FlexProps} props - Flex properties.
 * @returns {JSX.Element} Rendered flex div.
 */
export const Flex: React.FC<FlexProps> = ({ children, style, className, ...props }) => {
  return (
    <div {...filterDomProps(props)} className={cn("flex flex-basis-auto", className)} style={style}>
      {children}
    </div>
  );
};

/**
 * Flex row container.
 *
 * @param {FlexProps} props - FlexRow properties.
 * @returns {JSX.Element} Rendered flex-row div.
 */
export const FlexRow: React.FC<FlexProps> = ({ children, style, className, ...props }) => {
  return (
    <div {...filterDomProps(props)} className={cn("flex w-full justify-start items-start", className)} style={style}>
      {children}
    </div>
  );
};

/**
 * Flex column container.
 *
 * @param {FlexProps} props - FlexColumn properties.
 * @returns {JSX.Element} Rendered flex-col div.
 */
export const FlexColumn: React.FC<FlexProps> = ({ children, style, className, ...props }) => {
  return (
    <div {...filterDomProps(props)} className={cn("flex flex-col", className)} style={style}>
      {children}
    </div>
  );
};

/**
 * Centered flex container.
 *
 * @param {FlexProps} props - FlexCenter properties.
 * @returns {JSX.Element} Rendered centered flex div.
 */
export const FlexCenter = forwardRef<HTMLDivElement, FlexProps>((props, ref) => {
  const { children, style, className, ...restProps } = props;
  return (
    <div ref={ref} {...filterDomProps(restProps)} className={cn("flex justify-center items-center w-full", className)} style={style}>
      {children}
    </div>
  );
});

FlexCenter.displayName = 'FlexCenter';

/**
 * Flex spacer that fills available space.
 *
 * @param {FlexProps} props - FlexFill properties.
 * @returns {JSX.Element} Rendered spacer div.
 */
export const FlexFill: React.FC<FlexProps> = ({ style, className, ...props }) => {
  return <div className={cn("block flex-grow", className)} style={style} {...filterDomProps(props)} />;
};
